<script setup>
import MarkdownIt from 'markdown-it'
import { computed } from 'vue'

import { chatFormModeEnum, useChatStore } from '@/stores/chat.store'
import { searchResultTypeEnum, useWebSiteStore } from '@/stores/web-site.store'

const LINK_MARKUP_PATTERN = /\[\[([^|\]]+)\|([^\]]+)\]\]/g
const ENTITY_LINK_TARGET_PATTERN = /^(?:(new|edit):)?(\w+)(?::(\d+))?$/
// Private-use characters survive markdown rendering untouched, so entity links can be swapped in afterwards.
const PLACEHOLDER_PATTERN = /(\d+)/g
const FORM_MODES = Object.freeze({ new: chatFormModeEnum.CREATE, edit: chatFormModeEnum.EDIT })
const LINKABLE_TYPES = Object.freeze([
  searchResultTypeEnum.PROJECT,
  searchResultTypeEnum.QUOTE,
  searchResultTypeEnum.COMPANY,
  searchResultTypeEnum.CONTACT,
  searchResultTypeEnum.STAFF,
  searchResultTypeEnum.EQUIPMENT,
  searchResultTypeEnum.GROUP,
  searchResultTypeEnum.RATE_CARD,
])

const markdown = new MarkdownIt({ html: false, linkify: true, breaks: true })
const defaultLinkOpen = markdown.renderer.rules.link_open ?? ((tokens, index, options, env, self) => self.renderToken(tokens, index, options))
markdown.renderer.rules.link_open = (tokens, index, options, env, self) => {
  tokens[index].attrSet('target', '_blank')
  tokens[index].attrSet('rel', 'noopener noreferrer')
  return defaultLinkOpen(tokens, index, options, env, self)
}

const props = defineProps({
  text: {
    type: String,
    required: true,
  },
  formLinks: {
    type: Array,
    default: () => [],
  },
  hasDraft: {
    type: Boolean,
    default: false,
  },
})

const chatStore = useChatStore()
const webSiteStore = useWebSiteStore()

const matchingFormLink = (mode, type, id) => props.formLinks.find(link => link.mode === mode && link.entity_type === type && (link.entity_id ?? null) === id)

const toLink = (formMode, type, rawId, title) => {
  const id = Number(rawId) > 0 ? Number(rawId) : null
  if (!LINKABLE_TYPES.includes(type)) return null
  if (formMode) {
    const mode = FORM_MODES[formMode]
    if (mode === chatFormModeEnum.EDIT && !id) return null
    // The draft card already opens the creation form.
    if (mode === chatFormModeEnum.CREATE && props.hasDraft) return null
    return { text: title, type, form: matchingFormLink(mode, type, id) ?? { mode, entity_type: type, entity_id: id } }
  }
  return id ? { text: title, type, id } : null
}

const parsed = computed(() => {
  const links = []
  const source = props.text.replace(LINK_MARKUP_PATTERN, (token, target, title) => {
    const [, formMode, type, id] = target.trim().match(ENTITY_LINK_TARGET_PATTERN) ?? []
    const link = type ? toLink(formMode, type, id, title) : null
    if (!link) return title
    links.push(link)
    return `${links.length - 1}`
  })
  return { source, links }
})

const linkConfig = (type) => webSiteStore.getSearchResultTypeConfig(type)

const linkIcon = (link) => {
  if (!link.form) return linkConfig(link.type).icon
  return link.form.mode === chatFormModeEnum.EDIT ? 'mdi-pencil' : 'mdi-plus'
}

const chipHtml = (link, index) => {
  const { bgColor, textColor } = linkConfig(link.type)
  const label = markdown.utils.escapeHtml(link.text)
  return `<span role="button" tabindex="0" data-link="${index}" class="q-chip row inline no-wrap items-center q-chip--dense q-chip--clickable cursor-pointer non-selectable q-hoverable chat-entity-link bg-${bgColor} text-${textColor}">`
    + `<i class="q-icon notranslate mdi ${linkIcon(link)} q-chip__icon q-chip__icon--left" aria-hidden="true"></i>`
    + `<span class="q-chip__content col row no-wrap items-center"><span class="ellipsis">${label}</span></span></span>`
}

const html = computed(() => markdown.render(parsed.value.source).replace(PLACEHOLDER_PATTERN, (placeholder, index) => chipHtml(parsed.value.links[index], index)))

const trailingFormLinks = computed(() => {
  const linkedForms = new Set(parsed.value.links.map(link => link.form))
  return props.formLinks
    .filter(link => !linkedForms.has(link) && !(props.hasDraft && link.mode === chatFormModeEnum.CREATE))
    .map(link => ({ text: link.label, type: link.entity_type, form: link }))
})

const openLink = (link) => (link.form ? chatStore.openForm(link.form) : webSiteStore.openSearchResultAsync({ type: link.type, id: link.id, title: link.text }))

const onContentClick = (event) => {
  const index = event.target.closest('[data-link]')?.dataset.link
  if (index !== undefined) openLink(parsed.value.links[Number(index)])
}
</script>

<template>
  <div class="chat-message-text" @click="onContentClick" @keydown.enter="onContentClick">
    <!-- markdown-it runs with html disabled, so the model output cannot inject markup -->
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div class="chat-markdown" v-html="html" />
    <div v-if="trailingFormLinks.length" class="q-mt-xs">
      <q-chip
        v-for="link in trailingFormLinks"
        :key="link.form.label"
        dense
        clickable
        :color="linkConfig(link.type).bgColor"
        :text-color="linkConfig(link.type).textColor"
        :icon="linkIcon(link)"
        class="chat-entity-link"
        @click="openLink(link)"
      >
        <span class="ellipsis">{{ link.text }}</span>
        <q-tooltip>{{ link.text }}</q-tooltip>
      </q-chip>
    </div>
  </div>
</template>

<style scoped lang="scss">
.chat-message-text {
  word-break: break-word;
}

.chat-markdown {
  :deep(> :first-child) {
    margin-top: 0;
  }

  :deep(> :last-child) {
    margin-bottom: 0;
  }

  :deep(p),
  :deep(ul),
  :deep(ol),
  :deep(table),
  :deep(blockquote),
  :deep(pre) {
    margin: 0 0 .75rem;
  }

  :deep(ul),
  :deep(ol) {
    padding-left: 1.25rem;
  }

  :deep(li + li) {
    margin-top: .25rem;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4) {
    margin: 1rem 0 .5rem;
    font-size: 1rem;
    font-weight: 700;
    line-height: 1.4;
  }

  :deep(strong) {
    font-weight: 700;
  }

  :deep(a) {
    color: $primary;
  }

  :deep(code) {
    padding: .0625rem .25rem;
    border-radius: .25rem;
    background: rgba(0, 0, 0, 0.05);
    font-size: .8125rem;
  }

  :deep(pre) {
    padding: .5rem .625rem;
    border-radius: .375rem;
    background: rgba(0, 0, 0, 0.05);
    overflow-x: auto;

    code {
      padding: 0;
      background: none;
    }
  }

  :deep(blockquote) {
    padding-left: .625rem;
    border-left: 3px solid rgba($primary, 0.35);
    color: $grey-8;
  }

  :deep(table) {
    display: block;
    width: fit-content;
    max-width: 100%;
    overflow-x: auto;
    border: 1px solid $grey-3;
    border-radius: $app-border-radius;
    border-collapse: separate;
    border-spacing: 0;
    font-size: .8125rem;
  }

  :deep(th),
  :deep(td) {
    padding: .3125rem .5rem;
    border-right: 1px solid $grey-3;
    border-bottom: 1px solid $grey-3;
    text-align: left;
    white-space: nowrap;
  }

  :deep(th:last-child),
  :deep(td:last-child) {
    border-right: none;
  }

  :deep(tbody tr:last-child td) {
    border-bottom: none;
  }

  :deep(th) {
    background: $grid-focused-row-bg;
    font-weight: 600;
  }

  :deep(hr) {
    margin: .625rem 0;
    border: none;
    border-top: 1px solid $grey-3;
  }
}

.chat-entity-link,
.chat-markdown :deep(.chat-entity-link) {
  margin: 0 .125rem;
  max-width: 100%;
  vertical-align: middle;
}
</style>
